import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import Textarea from './Textarea';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

describe('Textarea', () => {
  it('renders a textarea', () => {
    render(<Textarea />);
    expect(screen.getByRole('textbox')).toBeInTheDocument();
  });

  it('renders label associated with textarea via htmlFor', () => {
    render(<Textarea id="bio" label="Bio" />);
    expect(screen.getByLabelText('Bio')).toBeInTheDocument();
  });

  it('renders required asterisk hidden from screen readers', () => {
    render(<Textarea label="Bio" required />);
    const asterisk = screen.getByText('*');
    expect(asterisk).toHaveAttribute('aria-hidden', 'true');
    expect(asterisk.tagName).toBe('SPAN');
  });

  it('renders placeholder', () => {
    render(<Textarea placeholder="Tell us about yourself" />);
    expect(screen.getByPlaceholderText('Tell us about yourself')).toBeInTheDocument();
  });

  it('renders default value', () => {
    render(<Textarea defaultValue="Hello world" />);
    expect(screen.getByRole('textbox')).toHaveValue('Hello world');
  });

  it('renders disabled state', () => {
    render(<Textarea disabled />);
    expect(screen.getByRole('textbox')).toBeDisabled();
  });

  it('renders required attribute on textarea', () => {
    render(<Textarea required />);
    expect(screen.getByRole('textbox')).toBeRequired();
  });

  it('renders hint text', () => {
    render(<Textarea hint="Maximum 500 characters" />);
    expect(screen.getByText('Maximum 500 characters')).toBeInTheDocument();
  });

  it('renders error message with alert role', () => {
    render(<Textarea error="Field is required" />);
    expect(screen.getByRole('alert')).toHaveTextContent('Field is required');
  });

  it('does not render alert role for hint', () => {
    render(<Textarea hint="Just a hint" />);
    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  it('renders error icon when error is present', () => {
    const { container } = render(<Textarea error="Error" />);
    expect(container.querySelector('[data-icon="triangleAlert"]')).toBeInTheDocument();
  });

  it('renders info icon for hint', () => {
    const { container } = render(<Textarea hint="Hint" />);
    expect(container.querySelector('[data-icon="info"]')).toBeInTheDocument();
  });

  it('renders character count', () => {
    render(<Textarea showCharacterCount maxLength={100} defaultValue="Hello" />);
    expect(screen.getByText('5 of 100 characters')).toBeInTheDocument();
  });

  it('updates character count on change', () => {
    render(<Textarea showCharacterCount maxLength={100} />);
    const textarea = screen.getByRole('textbox');
    fireEvent.change(textarea, { target: { value: 'abc' } });
    expect(screen.getByText('3 of 100 characters')).toBeInTheDocument();
  });

  describe('accessibility', () => {
    it('sets aria-invalid when error is present', () => {
      render(<Textarea error="Required" />);
      expect(screen.getByRole('textbox')).toHaveAttribute('aria-invalid', 'true');
    });

    it('does not set aria-invalid when no error', () => {
      render(<Textarea />);
      expect(screen.getByRole('textbox')).toHaveAttribute('aria-invalid', 'false');
    });

    it('associates error with textarea via aria-describedby', () => {
      render(<Textarea id="bio" error="Required" />);
      const textarea = screen.getByRole('textbox');
      expect(textarea).toHaveAttribute('aria-describedby', expect.stringContaining('bio-message'));
      expect(document.getElementById('bio-message')).toHaveTextContent('Required');
    });

    it('associates hint with textarea via aria-describedby', () => {
      render(<Textarea id="bio" hint="Keep it brief" />);
      const textarea = screen.getByRole('textbox');
      expect(textarea).toHaveAttribute('aria-describedby', expect.stringContaining('bio-message'));
    });

    it('includes character count in aria-describedby', () => {
      render(<Textarea id="bio" showCharacterCount maxLength={100} />);
      const textarea = screen.getByRole('textbox');
      expect(textarea).toHaveAttribute('aria-describedby', expect.stringContaining('bio-count'));
    });

    it('does not set aria-describedby when no message or count', () => {
      render(<Textarea id="bio" />);
      expect(screen.getByRole('textbox')).not.toHaveAttribute('aria-describedby');
    });

    it('character count has aria-live for screen readers', () => {
      const { container } = render(<Textarea showCharacterCount maxLength={100} />);
      const count = container.querySelector('[aria-live="polite"]');
      expect(count).toBeInTheDocument();
    });
  });

  describe('error sync and clearing', () => {
    it('syncs displayError when error prop changes', () => {
      const { rerender } = render(<Textarea error="First" />);
      expect(screen.getByText('First')).toBeInTheDocument();

      rerender(<Textarea error="Second" />);
      expect(screen.getByText('Second')).toBeInTheDocument();
    });

    it('clears displayError when error prop is removed', () => {
      const { rerender } = render(<Textarea error="Error" />);
      expect(screen.getByText('Error')).toBeInTheDocument();

      rerender(<Textarea />);
      expect(screen.queryByText('Error')).not.toBeInTheDocument();
    });

    it('clears error on change', () => {
      render(<Textarea error="Required" />);
      expect(screen.getByRole('alert')).toBeInTheDocument();

      fireEvent.change(screen.getByRole('textbox'), { target: { value: 'hello' } });
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('shows validation error on blur when required and empty', () => {
      render(<Textarea required defaultValidationMessage="Required field" />);
      fireEvent.blur(screen.getByRole('textbox'));
      expect(screen.getByRole('alert')).toHaveTextContent('Required field');
    });

    it('clears validation error on change after blur', () => {
      render(<Textarea required defaultValidationMessage="Required field" />);
      fireEvent.blur(screen.getByRole('textbox'));
      expect(screen.getByRole('alert')).toBeInTheDocument();

      fireEvent.change(screen.getByRole('textbox'), { target: { value: 'x' } });
      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });
  });
});
